/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import { humanizeDifficultyLevel } from 'bundles/unified-description-page-common/constants';
import type { ArticleRichEntityCardProps } from 'bundles/unified-seo-content-common/components/entity-cards/ArticleRichEntityCard';

import _t from 'i18n!nls/articles';

const styles = {
  card: css`
    min-height: 265px;
    border: 1px solid var(--cds-color-neutral-disabled);
    box-sizing: border-box;
    box-shadow: 0 0 10px var(--cds-color-darken-200);
    border-radius: 6px;
    text-align: left;
    padding: 30px 20px;
    margin: 25px 0;
    display: flex;

    ${breakpoints.down('sm')} {
      min-height: 180px;
      padding: 20px 16px;
    }
  `,
  titleContainer: css`
    display: inline-block;
    vertical-align: middle;
    max-width: 80%;
  `,
  image: css`
    display: inline-block;
    height: fit-content;
    border: 1px solid var(--cds-color-neutral-disabled);
    box-sizing: border-box;
    border-radius: 4px;
    padding: 5px;
    margin-right: 10px;

    img {
      max-width: 100%;
    }
  `,
  desktopView: css`
    display: flex;
  `,
  courseDetails: css`
    width: 65%;
    border-right: 1px solid var(--cds-color-grey-25);
    padding-right: 10px;

    .product-card-subtitle {
      margin-top: 10px;
    }

    a {
      width: 200px;
      height: 40px;
      margin-top: 25px;
      color: var(--cds-color-neutral-background-primary);
    }

    a:hover {
      color: var(--cds-color-neutral-primary-invert);
    }
  `,
  entityType: css`
    text-transform: capitalize;
  `,
  statistics: css`
    display: flex;
    margin-top: 10px;

    svg {
      margin: 2px 3px 0 0;
      color: var(--cds-color-emphasis-tertiary-background-default);
    }

    .ratings-count {
      padding: 0 var(--cds-spacing-100);
    }

    .ratings-count:first-of-type {
      padding-left: 0;
    }

    .vertical-divider {
      display: inline-block;
      width: 3px;
      height: 3px;
      background-color: var(--cds-color-grey-400);
      border-radius: var(--cds-spacing-50);
      position: relative;
      top: 8.5px;
    }

    .level {
      text-transform: lowercase;
      padding: 0 7px;
    }

    .level::first-letter {
      text-transform: capitalize;
    }
  `,
  duration: css`
    margin: 10px 0 0 38px;
    width: 30%;

    .skills-title {
      margin-top: 20px;
    }
  `,
  skillsList: css`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;

    ${breakpoints.down('sm')} {
      -webkit-line-clamp: 2;
      color: var(--cds-color-neutral-primary);

      .skills-title {
        display: inline-block;
      }

      .skills-body {
        display: inline;
        margin-left: 3px;
      }
    }
  `,
};

const DesktopEntityCard = ({
  ctaHref,
  ctaLabel,
  ctaAriaLabel,
  imageSrc,
  imageAlt,
  title,
  subtitle,
  cardMetaData,
}: ArticleRichEntityCardProps) => {
  const {
    entityType,
    level,
    durationInHours,
    durationInMonths,
    skills,
    averageFiveStarRating,
    ratingCount,
    totalEnrollmentCount,
  } = cardMetaData;
  return (
    <div css={styles.card} data-testid="DesktopEntityCard">
      <div css={styles.desktopView}>
        <div css={styles.courseDetails}>
          <div css={styles.image}>
            <LazyImgix src={imageSrc} alt={imageAlt} maxWidth={60} maxHeight={60} />
          </div>
          <div css={styles.titleContainer}>
            <Typography2 component="p" css={styles.entityType} variant="bodySecondary" color="supportText">
              {entityType}
            </Typography2>
            <Typography variant="h2semibold" component="h3">
              {title}
            </Typography>
          </div>
          <Typography2 component="p" variant="bodySecondary" color="supportText" className="product-card-subtitle">
            {subtitle}
          </Typography2>
          <div css={styles.statistics}>
            {!!averageFiveStarRating && (
              <>
                <StarFilledIcon size="small" />
                <Typography2 variant="subtitleMedium" component="p">
                  {averageFiveStarRating}
                </Typography2>
              </>
            )}
            {!!ratingCount && (
              <>
                <Typography2 component="p" variant="bodySecondary" color="supportText" className="ratings-count">
                  {_t('(#{ratingCount} ratings)', {
                    ratingCount: ratingCount.toLocaleString(),
                  })}
                </Typography2>
                <div className="vertical-divider" />
              </>
            )}
            <Typography2 component="p" variant="bodySecondary" color="supportText" className="ratings-count">
              {_t('#{totalEnrollmentCount} already enrolled', {
                totalEnrollmentCount,
              })}
            </Typography2>
            <div className="vertical-divider" />
            {level && (
              <Typography2 component="p" variant="bodySecondary" color="supportText" className="level">
                {_t('#{level} level', {
                  level: humanizeDifficultyLevel(level),
                })}
              </Typography2>
            )}
          </div>

          <Button
            variant="primary"
            component={TrackedLink2}
            trackingName="article_rich_product_card"
            withVisibilityTracking
            href={ctaHref}
            aria-label={ctaAriaLabel}
          >
            {ctaLabel || _t('Learn More')}
          </Button>
        </div>

        <div css={styles.duration}>
          {!!durationInMonths && !durationInHours && (
            <Typography2 variant="subtitleMedium" component="p">
              {_t('Average time: #{durationInMonths} month(s)', { durationInMonths })}
            </Typography2>
          )}
          {!!durationInHours && !durationInMonths && (
            <Typography2 variant="subtitleMedium" component="p">
              {_t('Average time: #{durationInHours} hour(s)', { durationInHours })}
            </Typography2>
          )}
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {_t('Learn at your own pace')}
          </Typography2>
          {skills && (
            <>
              <Typography2 variant="subtitleMedium" component="p" className="skills-title">
                {_t("Skills you'll build:")}
              </Typography2>
              <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.skillsList}>
                {skills}
              </Typography2>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesktopEntityCard;
